import type { KnowledgeFigure } from "./types.js";

/** Reviewed light-mode product captures and client-neutral method diagrams. */
export const KNOWLEDGE_FIGURES: Readonly<Record<string, KnowledgeFigure>> = {
  "solve-five-phases": {
    id: "solve-five-phases",
    kind: "diagram",
    src: new URL("../assets/solve-five-phases.webp", import.meta.url).href,
    alt: "SOLVE phases: Specify — define the gap; Oppress — contain the impact; Look — find the cause; Verify — test the change; Ensure — keep it working. Arrows connect the phases in this order.",
    caption:
      "Start with the gap, then work towards a lasting change. Return to an earlier phase when new evidence changes the explanation.",
    width: 2056,
    height: 765,
  },
  "vsm-work-and-waiting": {
    id: "vsm-work-and-waiting",
    kind: "diagram",
    src: new URL("../assets/vsm-work-and-waiting.webp", import.meta.url).href,
    alt: "A fictional service request: check the request for 5 minutes, wait for approval for 50 minutes, then approve it for 5 minutes. Work time is 10 minutes, waiting time is 50 minutes and total lead time is 60 minutes.",
    caption:
      "Ten minutes of work and 50 minutes of waiting make 60 minutes of lead time in this fictional, sequential request.",
    width: 1942,
    height: 809,
  },
  "platform-launcher": {
    id: "platform-launcher",
    src: new URL("../assets/platform-launcher.jpg", import.meta.url).href,
    alt: "App launcher with SOLVE, VSM, Hoshin, LAMA, Knowledge Studio and Settings.",
    caption: "The launcher lists the apps enabled for your workspace.",
    width: 1728,
    height: 936,
  },
  "platform-preferences": {
    id: "platform-preferences",
    src: new URL("../assets/platform-preferences.jpg", import.meta.url).href,
    alt: "Light theme selected beside Match device, Dark and High contrast controls, followed by time zone.",
    caption:
      "Account settings keep appearance and time-zone preferences together.",
    width: 1100,
    height: 375,
  },
  "player-ai-preferences": {
    id: "player-ai-preferences",
    src: new URL("../assets/player-ai-preferences.jpg", import.meta.url).href,
    alt: "Player AI General settings with Direct and Empathetic tone, English, mood-history access and Auto approval controls.",
    caption:
      "Player AI preferences include tone, language, mood-history access, auto approval and the default profile.",
    width: 1110,
    height: 780,
  },
  "solve-create": {
    id: "solve-create",
    src: new URL("../assets/solve-create.jpg", import.meta.url).href,
    alt: "Create SOLVE dialog describing late service requests with a measured starting condition.",
    caption:
      "Create a SOLVE with a name and a description of the observed gap. This service example is fictional.",
    width: 560,
    height: 511,
  },
  "solve-widget-picker": {
    id: "solve-widget-picker",
    src: new URL("../assets/solve-widget-picker.jpg", import.meta.url).href,
    alt: "SOLVE Add widget menu with phase tools and flexible widgets.",
    caption:
      "Add widget separates phase tools from flexible content you can use where it helps.",
    width: 320,
    height: 440,
  },
  "solve-specify": {
    id: "solve-specify",
    src: new URL("../assets/solve-specify.jpg", import.meta.url).href,
    alt: "SOLVE Specify canvas with a Problem Statement widget about late service requests.",
    caption:
      "A fictional service gap shown in the Specify workspace. Use the statement to frame the investigation.",
    width: 1728,
    height: 936,
  },
  "solve-people": {
    id: "solve-people",
    src: new URL("../assets/solve-people.jpg", import.meta.url).href,
    alt: "SOLVE People panel showing the workspace owner and access controls.",
    caption:
      "People shows access to this SOLVE, separately from roles elsewhere in the app.",
    width: 1728,
    height: 936,
  },
  "vsm-current-map": {
    id: "vsm-current-map",
    src: new URL("../assets/vsm-current-map.jpg", import.meta.url).href,
    alt: "Overview of a fictional current-state map with five lanes, material and information arrows, and a lead-time ladder.",
    caption:
      "Current state has its own map, symbol palette and lead-time ladder. This fictional manufacturing map shows the overall layout.",
    width: 1728,
    height: 840,
  },
  "vsm-palette": {
    id: "vsm-palette",
    src: new URL("../assets/vsm-palette.jpg", import.meta.url).href,
    alt: "Expanded VSM symbol palette, including essentials and material flow arrows.",
    caption:
      "The palette separates process symbols, material flow and information flow. Search for the symbol you need.",
    width: 320,
    height: 888,
  },
  "vsm-process-data": {
    id: "vsm-process-data",
    src: new URL("../assets/vsm-process-data.jpg", import.meta.url).href,
    alt: "Dedicated Process data box showing cycle time, changeover time, uptime and empty operator and waiting-time fields.",
    caption:
      "The data box keeps observed Facts, proposed Targets and Connections separate. The values here are fictional.",
    width: 960,
    height: 768,
  },
  "vsm-sipoc": {
    id: "vsm-sipoc",
    src: new URL("../assets/vsm-sipoc.jpg", import.meta.url).href,
    alt: "Populated fictional SIPOC process with Suppliers, Inputs, Process, Outputs and Customers columns.",
    caption:
      "SIPOC keeps suppliers, inputs, process, outputs and customers aligned around each step. This example is fictional.",
    width: 1728,
    height: 840,
  },
  "vsm-kaizen-health": {
    id: "vsm-kaizen-health",
    src: new URL("../assets/vsm-kaizen-health.jpg", import.meta.url).href,
    alt: "VSM Monitor Kaizen health summary with Overall, On track, At risk, Blocked, Overdue, Unassigned, Not promoted and Done categories.",
    caption:
      "Monitor summarises Kaizen health. Use it to find work that needs attention, then inspect the action and its evidence.",
    width: 403,
    height: 420,
  },
  "hoshin-create": {
    id: "hoshin-create",
    src: new URL("../assets/hoshin-create.jpg", import.meta.url).href,
    alt: "Create Hoshin dialog with Reliable service name and a description about reducing late requests.",
    caption:
      "Name the Hoshin plan for the direction it supports. This service example is fictional.",
    width: 560,
    height: 384,
  },
  "hoshin-ideate": {
    id: "hoshin-ideate",
    src: new URL("../assets/hoshin-ideate.jpg", import.meta.url).href,
    alt: "Empty Hoshin Ideate area with Add a new idea and Submit ideas controls.",
    caption:
      "Ideate starts with your own idea area. Add ideas, then submit them for the workshop.",
    width: 1728,
    height: 840,
  },
  "hoshin-x-matrix": {
    id: "hoshin-x-matrix",
    src: new URL("../assets/hoshin-x-matrix.jpg", import.meta.url).href,
    alt: "Expanded X-Matrix with linked long-term and annual service objectives, a daily review priority, an on-time resolution target and primary owner.",
    caption:
      "The X-Matrix connects long-term direction, annual objectives, priorities, measures and owners. This service example is fictional.",
    width: 1636,
    height: 830,
  },
  "hoshin-monitor": {
    id: "hoshin-monitor",
    src: new URL("../assets/hoshin-monitor.jpg", import.meta.url).href,
    alt: "Empty Bowling chart with target and actual columns, date range and status filters.",
    caption:
      "A new plan has nothing to monitor until its X-Matrix contains objectives and targets. Those commitments then appear in the Bowling chart.",
    width: 1728,
    height: 700,
  },
  "lama-assessment": {
    id: "lama-assessment",
    src: new URL("../assets/lama-assessment.jpg", import.meta.url).href,
    alt: "LAMA assessment question asking about obstacles to daily standard work, with Answer and Comment fields and Review and submit.",
    caption:
      "An assessment question asks for your answer and optional context. Review the whole response before submitting.",
    width: 870,
    height: 625,
  },
  "admin-add-people": {
    id: "admin-add-people",
    src: new URL("../assets/admin-add-people.jpg", import.meta.url).href,
    alt: "Admin Members and roles Add people tab with Microsoft Entra, email invitation and local-account sections.",
    caption:
      "Add people offers directory, invitation and local-account workflows. The role controls are independent.",
    width: 552,
    height: 984,
  },
  "admin-identity": {
    id: "admin-identity",
    src: new URL("../assets/admin-identity.jpg", import.meta.url).href,
    alt: "Admin Identity Overview with Microsoft Entra sign-in, directory sync, shared Teams and access sync status cards.",
    caption:
      "Identity Overview separates sign-in, directory sync, shared Teams and access sync. These are local demonstration counts.",
    width: 1440,
    height: 960,
  },
  "admin-provisioning": {
    id: "admin-provisioning",
    src: new URL("../assets/admin-provisioning.jpg", import.meta.url).href,
    alt: "Identity Provisioning tab with no OAuth clients and a Generate credentials control.",
    caption:
      "Provisioning starts with the OAuth clients used by your identity provider. This example has no clients yet.",
    width: 552,
    height: 984,
  },
  "admin-organization": {
    id: "admin-organization",
    src: new URL("../assets/admin-organization.jpg", import.meta.url).href,
    alt: "Organization name, short name and company-domain fields with guidance; no tenant identifiers shown.",
    caption:
      "The profile editor explains how the name, short name and domain are used. A company domain does not control sign-in.",
    width: 450,
    height: 408,
  },
  "admin-modules": {
    id: "admin-modules",
    src: new URL("../assets/admin-modules.jpg", import.meta.url).href,
    alt: "Admin Modules page with availability switches for five apps and a separate Email capability.",
    caption:
      "App availability and workspace capabilities have separate controls.",
    width: 1440,
    height: 960,
  },
  "admin-mail-server": {
    id: "admin-mail-server",
    src: new URL("../assets/admin-mail-server.jpg", import.meta.url).href,
    alt: "Blank Add mail server form with SMTP host, port, sender email, display name, username and password secret-reference fields.",
    caption:
      "The mail-server form asks for sender details, the SMTP endpoint and a secret reference. The visible examples are placeholders.",
    width: 504,
    height: 806,
  },
  "admin-templates": {
    id: "admin-templates",
    src: new URL("../assets/admin-templates.jpg", import.meta.url).href,
    alt: "Email Message templates tab listing invitation, access-granted, email-verification and password-reset messages.",
    caption: "Choose a message template before editing its wording.",
    width: 552,
    height: 984,
  },
  "admin-ai-connection": {
    id: "admin-ai-connection",
    src: new URL("../assets/admin-ai-connection.jpg", import.meta.url).href,
    alt: "Create AI connection form with connection name, provider, authentication, secret reference and Save connection.",
    caption:
      "Store provider access through a connection. The form uses a secret reference; no credential is shown.",
    width: 504,
    height: 561,
  },
  "admin-ai-defaults": {
    id: "admin-ai-defaults",
    src: new URL("../assets/admin-ai-defaults.jpg", import.meta.url).href,
    alt: "GenAI App defaults tab with platform, Home, SOLVE, Hoshin and VSM profile selectors and Save defaults.",
    caption:
      "Apps can inherit the platform profile or use an explicit default. Chat delivery is configured separately.",
    width: 1140,
    height: 790,
  },
  "admin-integration": {
    id: "admin-integration",
    src: new URL("../assets/admin-integration.jpg", import.meta.url).href,
    alt: "Add integration form for an Atlassian MCP connector with blank name, description and site URL.",
    caption:
      "Choose a supported connector, then configure the specific account or system you need. This blank form contains no connected account.",
    width: 552,
    height: 984,
  },
  "admin-adoption-tours": {
    id: "admin-adoption-tours",
    src: new URL("../assets/admin-adoption-tours.webp", import.meta.url).href,
    alt: "Onboarding tours controls with completed counts and individual Reset tour buttons.",
    caption:
      "Tour replay offers guidance again; it does not change permissions or erase workspace work.",
    width: 1090,
    height: 380,
  },
  "admin-audit": {
    id: "admin-audit",
    src: new URL("../assets/admin-audit.jpg", import.meta.url).href,
    alt: "Audit history source-service, action, actor and date filters, plus Include routine activity switch; no private event rows.",
    caption:
      "Narrow the audit timeline by service, action, actor and date before inspecting a record.",
    width: 1140,
    height: 450,
  },
};

export { ARTICLE_FIGURES } from "@playbookai/knowledge-content";
